import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ServerCommunicationUtil } from '../utils/server_communication_util';
import { Dropzone } from 'dropzone';

/*
  The following callbacks need to be passed in with a useCallback function because updates to these
  methods will need to be added to the dropzone instance. Failure to use the useCallback for these callbacks
  will result in an infinite loop.

  addedFileCallback
  thumbnailCallback
  removedFileCallback
  successCallback
  sendingCallback
  progressCallback
  queueCompleteCallback
  handleErrorCallback
*/

function useDropzoneUpload({
  url,
  method = 'POST',
  paramName,
  formParam,
  acceptedFiles,
  maxFiles,
  maxFilesize,
  addedFileCallback = () => {},
  thumbnailCallback = (file, dataUrl) => {},
  removedFileCallback = (file, response) => {},
  successCallback = (file, response) => {},
  sendingCallback = (file, response) => {},
  progressCallback = fileProgress => {},
  queueCompleteCallback = () => {},
  handleErrorCallback = (file, errorMessage = null) => {},
  disabled
}) {
  const [dropzone, initDropzone] = useState(null);

  const removeDuplicateFiles = (file, files = [], removeFile) => {
    // we use files.length - 1 so it excludes the recently added file
    for (let i = 0; i < files.length - 1; i++) {
      if (files[i].name === file.name && files[i].size === file.size && files[i].lastModified === file.lastModified) {
        removeFile(file);
      }
    }
  };

  useEffect(() => {
    if (disabled) return;

    const dz = new Dropzone(formParam, {
      url,
      method,
      paramName,
      params: { key: '3p0t5s6b5g4g0e8k3c1j3w7y5c3m4t8i' },
      autoProcessQueue: false,
      acceptedFiles,
      clickable: true,
      parallelUploads: maxFiles,
      maxFiles,
      autoDiscover: false,
      maxFilesize,
      timeout: 60000,
      addRemoveLinks: true,
      previewsContainer: '#previewsContainer',
      headers: ServerCommunicationUtil.defaultAuthHeaders(),
      removedfile: () => {} // since we are not using the html given from the plugin, we dont want to try and remove any nodes that do not exist
    });

    initDropzone(dz);

    return () => {
      dz.destroy();
      initDropzone(null);
    };
  }, []);

  useEffect(() => {
    if (!dropzone) return;

    dropzone.off('error')
    dropzone.on('error', function (file, errorMessage, xhr) {
      if (errorMessage === "You can't upload files of this type.") {
        this.removeFile(file);
        return;
      }

      let parsedErrorMessage;
      if (errorMessage?.errors instanceof Array) {
        parsedErrorMessage = errorMessage.errors[0]?.message;
      } else if (errorMessage instanceof String) {
        parsedErrorMessage = errorMessage;
      }

      handleErrorCallback(file, parsedErrorMessage);
    });
  }, [dropzone, handleErrorCallback]);

  useEffect(() => {
    if (!dropzone) return;

    dropzone.off('totaluploadprogress')
    dropzone.on('totaluploadprogress', function (progress) {
      progressCallback(progress);
    });
  }, [dropzone, progressCallback]);

  useEffect(() => {
    if (!dropzone) return;

    dropzone.off('success')
    dropzone.on('success', function (file, response) {
      successCallback(file, response);
    });
  }, [dropzone, successCallback]);

  useEffect(() => {
    if (!dropzone) return;

    dropzone.off('removedFile')
    dropzone.on('removedfile', function (file) {
      removedFileCallback(file);
    });
  }, [dropzone, removedFileCallback]);

  useEffect(() => {
    if (!dropzone) return;

    dropzone.off('thumbnail')
    dropzone.on('thumbnail', function (file, dataUrl) {
      thumbnailCallback(file, dataUrl);
    });
  }, [dropzone, thumbnailCallback]);

  useEffect(() => {
    if (!dropzone) return;

    dropzone.off('addedfile')
    dropzone.on('addedfile', function (file) {
      // this.removeFile is defined in the Dropzone plugin itself
      removeDuplicateFiles(file, this.files, this.removeFile);
      addedFileCallback(file);
    });
  }, [dropzone, addedFileCallback]);

  useEffect(() => {
    if (!dropzone) return;

    dropzone.off('sending')
    dropzone.on('sending', function (file, xhr, formData) {
      sendingCallback(file, formData);
    });
  }, [sendingCallback, dropzone]);

  useEffect(() => {
    if (!dropzone) return;

    dropzone.off('queuecomplete')
    dropzone.on('queuecomplete', function () {
      let hasErrors = false;
      const failedUploads = this.files.filter(file => {
        return file.status === 'error';
      });

      if (failedUploads.length > 0) {
        hasErrors = true;
        failedUploads.forEach(upload => {
          upload.status = Dropzone.QUEUED;
        });
      }

      queueCompleteCallback(hasErrors);
    });
  }, [dropzone, queueCompleteCallback]);

  return [dropzone, initDropzone];
}

export default useDropzoneUpload;
